import React, {
  type ComponentProps,
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  type ScrollViewInstance,
  StyleSheet,
  View,
  type ViewInstance,
  type ViewProps,
  type ViewStyle,
} from 'react-native';

// ─────────────────────────────────────────────────────────────────────────────

export const CustomizationSelectedIngredientsList = (
  props: CustomizationSelectedIngredientsListProps,
) => {
  const { children, style, ...restProps } = props;

  return (
    <View style={[styles.container, style]} {...restProps}>
      {children}
    </View>
  );
};

export const CustomizationSelectedIngredientsListItem = (
  props: CustomizationSelectedIngredientsListItemProps,
) => {
  const { children, style, isActive, scrollViewRef, shouldScrollToSelf, ...restProps } = props;

  // ─── Refs ────────────────────────────────────────────────────────────

  const containerRef = useRef<ViewInstance>(null);

  // ─── Styles ──────────────────────────────────────────────────────────

  const dynamicStyles = useMemo<ViewStyle>(
    () => ({ zIndex: isActive ? 1 : undefined }),
    [isActive],
  );

  // ─── Helpers ─────────────────────────────────────────────────────────

  const scrollToSelf = useCallback(
    (_x: number, y: number) => {
      const scrollOffset = 80;

      scrollViewRef?.current?.scrollTo({ y: y - scrollOffset });
    },
    [scrollViewRef],
  );

  const measureLayout = useCallback(
    (onMeasure: (x: number, y: number) => void) => {
      const innerViewNode = scrollViewRef?.current?.getInnerViewNode();

      if (!shouldScrollToSelf || innerViewNode === undefined) {
        return;
      }

      containerRef.current?.measureLayout(innerViewNode, onMeasure);
    },
    [scrollViewRef, shouldScrollToSelf],
  );

  // ─── Effects ─────────────────────────────────────────────────────────

  useEffect(() => {
    if (!shouldScrollToSelf) return;

    measureLayout(scrollToSelf);
  }, [measureLayout, scrollToSelf, scrollViewRef, shouldScrollToSelf]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View ref={containerRef} style={[styles.listItem, dynamicStyles, style]} {...restProps}>
      {children}
    </View>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    alignItems: 'flex-start',
  },
  listItem: {
    width: '100%',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CustomizationSelectedIngredientsListProps = ComponentProps<typeof View>;

type CustomizationSelectedIngredientsListItemProps = {
  // NOTE: For some reason, TS was not happy with `LI` component's default styles.
  style?: ViewProps['style'];
  isActive?: boolean;
  shouldScrollToSelf?: boolean;
  scrollViewRef?: RefObject<ScrollViewInstance | null>;
} & Omit<ComponentProps<typeof View>, 'style'>;
